import { createClient } from '@connectrpc/connect';
import { FamilyService, QuickFillSnapshotShape } from './gen/family_service_pb';
import { pollRevitOperation, type RevitOperationProgressSnapshot } from './operationProgressClient';
import { getGrpcTransport } from './transport';
import { useIntegrationDiagnosticsStore } from '../../stores/useIntegrationDiagnosticsStore';
import { webLog } from './webLogger';
import {
  normalizeRequestedParameterIntents,
  parseAuthoritativeModelVersion,
  type RequestedParameterIntentInput,
} from './requestedParameterIntent';

export type QuickFillSnapshotShapeOption = 'familyType' | 'instanceOnly';

export interface QuickFillDataScope {
  familyIds?: string[];
  familyNames?: string[];
  familyTypeKeys?: string[];
  elementIds?: string[];
  categoryNames?: string[];
}

interface QuickFillDataResult {
  success: boolean;
  mappingReadinessScoped?: boolean;
  families?: any[];
  availableParameters?: any[];
  pipeElements?: any[];
  modelVersion?: number;
  error?: string;
}

function getClient() {
  const transport = getGrpcTransport();
  return transport ? createClient(FamilyService, transport) : null;
}

const normalizeScopeValues = (values?: string[]): string[] =>
  Array.from(new Set((values ?? []).map((value) => value.trim()).filter(Boolean)));

function buildQuickFillDataRequest(
  parameterNames: string[] | undefined,
  snapshotShape: QuickFillSnapshotShapeOption | undefined,
  dataScope: QuickFillDataScope | undefined,
  deferUnplacedFamilyCatalog: boolean,
  requestedParameters?: readonly RequestedParameterIntentInput[],
  mappingReadinessOnly = false
) {
  return {
    parameterNames: parameterNames ?? [],
    snapshotShape: snapshotShape === 'instanceOnly'
      ? QuickFillSnapshotShape.INSTANCE_ONLY
      : QuickFillSnapshotShape.FAMILY_TYPE,
    scopedFamilyIds: normalizeScopeValues(dataScope?.familyIds),
    scopedFamilyNames: normalizeScopeValues(dataScope?.familyNames),
    scopedFamilyTypeKeys: normalizeScopeValues(dataScope?.familyTypeKeys),
    scopedElementIds: normalizeScopeValues(dataScope?.elementIds),
    scopedCategoryNames: normalizeScopeValues(dataScope?.categoryNames),
    deferUnplacedFamilyCatalog,
    requestedParameters: normalizeRequestedParameterIntents(requestedParameters),
    mappingReadinessOnly,
  };
}

export const __quickFillClientTestInternals = { buildQuickFillDataRequest };

function parseQuickFillDataResultJson(resultJson: string): QuickFillDataResult {
  const parsed = JSON.parse(resultJson);
  return {
    success: Boolean(parsed.isSuccess),
    mappingReadinessScoped: Boolean(parsed.mappingReadinessScoped),
    families: parsed.families ?? [],
    availableParameters: parsed.availableParameters ?? [],
    pipeElements: parsed.pipeElements ?? [],
    modelVersion: parseAuthoritativeModelVersion(parsed.modelVersion),
    error: parsed.error || undefined,
  };
}

export async function getQuickFillDataGrpc(
  parameterNames?: string[],
  options?: {
    onProgress?: (progress: RevitOperationProgressSnapshot) => void;
    snapshotShape?: QuickFillSnapshotShapeOption;
    dataScope?: QuickFillDataScope;
    deferUnplacedFamilyCatalog?: boolean;
    requestedParameters?: readonly RequestedParameterIntentInput[];
    mappingReadinessOnly?: boolean;
  }
): Promise<QuickFillDataResult> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('GetQuickFillData: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  const request = buildQuickFillDataRequest(
    parameterNames,
    options?.snapshotShape,
    options?.dataScope,
    options?.deferUnplacedFamilyCatalog === true,
    options?.requestedParameters,
    options?.mappingReadinessOnly === true
  );
  try {
    try {
      const startResponse = await client.startQuickFillLoad(request);
      if (startResponse.isSuccess && startResponse.operationId) {
        const operationResult = await pollRevitOperation(startResponse.operationId, {
          workload: { parameterCount: request.parameterNames.length },
          onProgress: options?.onProgress,
          parseResult: parseQuickFillDataResultJson,
        });
        if (!operationResult.success) {
          return { success: false, error: operationResult.error || 'Quick Fill operation failed' };
        }

        if (operationResult.result?.success) {
          useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
        }
        return operationResult.result ?? { success: false, error: 'Quick Fill operation returned no result' };
      }
    } catch (startError) {
      const message = startError instanceof Error ? startError.message : 'startQuickFillLoad gRPC error';
      webLog.warn('quickFillClient', `async snapshot start unavailable (${message}); falling back to unary`);
    }

    const response = await client.getQuickFillData(request);
    const result = {
      success: response.isSuccess,
      mappingReadinessScoped: response.mappingReadinessScoped,
      families: response.families,
      availableParameters: response.availableParameters,
      pipeElements: response.pipeElements,
      modelVersion: parseAuthoritativeModelVersion(response.modelVersion),
      error: response.error || undefined,
    };
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(
      result.success ? null : `GetQuickFillData: ${result.error || 'request failed'}`
    );
    return result;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'GetQuickFillData gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetQuickFillData: ${message}`);
    return { success: false, error: message };
  }
}
